const toNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toNumberOrZero = (value) => {
  const n = toNumberOrNull(value);
  return n === null ? 0 : n;
};

const pickFromSubscriptionThenEntity = (entity, keys) => {
  const subscription = entity?.subscription || {};
  for (const key of keys) {
    if (subscription[key] !== undefined && subscription[key] !== null) {
      return subscription[key];
    }
    if (entity?.[key] !== undefined && entity?.[key] !== null) {
      return entity[key];
    }
  }
  return null;
};

export const getAgentPlanName = (agent) =>
  agent?.subscription?.plan_name || agent?.plan_name || agent?.subscription_plan_name || null;

export const getRemainingMessageCount = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'remaining_message_count',
    'subscription_remaining_message_count',
    'minutes_remaining',
    'subscription_minutes_remaining',
  ]);
  return toNumberOrNull(value);
};

/** Voice / pooled minutes left (subscription). Prefer for a dedicated “Minutes remaining” column. */
export const getMinutesRemaining = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'minutes_remaining',
    'subscription_minutes_remaining',
    'remaining_voice_minutes',
    'voice_minutes_remaining',
    'included_minutes_remaining',
  ]);
  return toNumberOrNull(value);
};

/** SMS / chat messages left (subscription). Prefer for “Messages remaining” distinct from voice minutes. */
export const getMessagesRemaining = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'remaining_message_count',
    'subscription_remaining_message_count',
    'messages_remaining',
  ]);
  return toNumberOrNull(value);
};

export const getUsedMessageCount = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'used_message_count',
    'subscription_used_message_count',
    'minutes_used',
    'subscription_minutes_used',
  ]);
  return toNumberOrNull(value);
};

export const getTotalMessageCount = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'total_message_count',
    'subscription_total_message_count',
    'minutes_total',
    'included_minutes',
    'minutes_per_month',
  ]);
  return toNumberOrNull(value);
};

/** Facebook message pool (subscription), same shape as general message counters */
export const getFacebookRemainingMessageCount = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'remaining_facebook_message_count',
    'subscription_remaining_facebook_message_count',
  ]);
  return toNumberOrNull(value);
};

export const getFacebookUsedMessageCount = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'used_facebook_message_count',
    'subscription_used_facebook_message_count',
  ]);
  return toNumberOrNull(value);
};

export const getFacebookTotalMessageCount = (entity) => {
  const value = pickFromSubscriptionThenEntity(entity, [
    'total_facebook_message_count',
    'subscription_total_facebook_message_count',
  ]);
  return toNumberOrNull(value);
};

/**
 * Show Facebook quota UI only when messaging is active for this agent: explicitly enabled and/or
 * a non-zero Facebook pool from GET /agents or GET /agents/:id (normalized subscription + root aliases).
 */
export const isAgentFacebookMessagingActive = (entity) => {
  if (!entity) return false;
  if (entity.facebook_enabled === true) return true;
  const total = getFacebookTotalMessageCount(entity);
  if (total !== null && total > 0) return true;
  const rem = getFacebookRemainingMessageCount(entity);
  const used = getFacebookUsedMessageCount(entity);
  return (rem ?? 0) > 0 || (used ?? 0) > 0;
};

export const getPurchasedMessageCount = (entity) => {
  const explicitTotal = getTotalMessageCount(entity);
  if (explicitTotal !== null && explicitTotal > 0) return explicitTotal;
  return toNumberOrZero(getRemainingMessageCount(entity)) + toNumberOrZero(getUsedMessageCount(entity));
};

export const formatCount = (value) => toNumberOrZero(value).toLocaleString();
